import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { toast } from "sonner";
import { SearchInput } from "@/components/ui/search-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  proveedoresAPI,
  type Proveedor
} from "@/lib/api";
import { dataRefresh } from "@/lib/dataRefresh";
import { mensajeBajaBloqueada } from "@/lib/bajaCopy";

// CUIT formatting helper: formats to XX-XXXXXXXX-X
const formatCUIT = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '').slice(0, 11);

  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
  }
};

const emptyFormData: Proveedor = {
  codigo: "",
  nombreRazonSocial: "",
  cuit: "",
  domicilio: "",
  localidad: "",
  provincia: "",
  personaContacto: "",
  telefono: "",
  celular: "",
  whatsapp: "",
  email: "",
  saldo: 0,
  activo: true
};

interface ProveedoresABMProps {
  onProveedorSaved?: (codigo?: string) => void;
  createOnly?: boolean;  // If true, hide search and delete button
}

export function ProveedoresABM({ onProveedorSaved, createOnly = false }: ProveedoresABMProps) {
  const [formData, setFormData] = useState<Proveedor>(emptyFormData);
  const [originalFormData, setOriginalFormData] = useState(formData);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredProveedores, setFilteredProveedores] = useState<Proveedor[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Load suppliers for suggestions (ABM search). createOnly no usa listado.
  useEffect(() => {
    if (createOnly) return;
    loadProveedores();
  }, [createOnly]);

  const loadProveedores = async () => {
    try {
      const data = await proveedoresAPI.list({ activo: true });
      setFilteredProveedores(data);
    } catch (error) {
      console.error("Failed to load proveedores suggestions", error);
    }
  };

  const handleNombreChange = (value: string) => {
    setFormData({ ...formData, nombreRazonSocial: value });
    checkForChanges({ ...formData, nombreRazonSocial: value });

    // Only search if we're NOT in "creating new" mode (no codigo means editing/searching)
    if (!formData.codigo && value.trim()) {
      (async () => {
        try {
          const results = await proveedoresAPI.list({ search: value });
          setFilteredProveedores(results);
          // Only show suggestions if there are results
          setShowSuggestions(results.length > 0);
        } catch (e) { console.error(e); }
      })();
    } else {
      setShowSuggestions(false);
    }
  };

  const selectProveedor = (proveedor: typeof filteredProveedores[0]) => {
    const loadedData = { ...proveedor };
    setFormData(loadedData);
    setOriginalFormData(loadedData);
    setHasChanges(false);
    setShowSuggestions(false);
  };

  const handleNombreKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
    // Note: Removed Enter/Tab auto-selection - use "Nuevo Proveedor" button instead
  };

  const handleNuevo = async () => {
    try {
      setShowSuggestions(false); // Hide dropdown immediately
      const { codigo } = await proveedoresAPI.getNextCodigo();
      const newProveedor: Proveedor = {
        codigo,
        nombreRazonSocial: "",
        cuit: "",
        domicilio: "",
        localidad: "",
        provincia: "",
        personaContacto: "",
        telefono: "",
        celular: "",
        whatsapp: "",
        email: "",
        saldo: 0,
        activo: true
      };
      setFormData(newProveedor);
      setOriginalFormData({ ...newProveedor, codigo: "" }); // Mark as new (no original code)
      setHasChanges(true);
      setFilteredProveedores([]); // Clear suggestions list
      toast.success(`Creando nuevo proveedor: ${codigo}`);
    } catch {
      toast.error("Error al generar código");
    }
  };

  const checkForChanges = (data: typeof formData) => {
    const changed = JSON.stringify(data) !== JSON.stringify(originalFormData);
    setHasChanges(changed);
  };

  const handleInputChange = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    checkForChanges(newData);
  };

  const handleSave = async () => {
    // Auto-generate code if creating new proveedor and no code exists
    const currentFormData = { ...formData };

    if (!currentFormData.nombreRazonSocial.trim()) {
      toast.error("Nombre o Razón Social es requerido");
      return;
    }

    // Prepare promise for toast
    const savePromise = (async () => {
      let result;

      // If no code, fetch next one first
      if (!currentFormData.codigo) {
        try {
          const { codigo } = await proveedoresAPI.getNextCodigo();
          currentFormData.codigo = codigo;
        } catch {
          throw new Error("Error generando código de proveedor");
        }
      }

      if (originalFormData.codigo) {
        // Update
        result = await proveedoresAPI.update(currentFormData.codigo, currentFormData);
        setFormData(result);
        setOriginalFormData(result);
      } else {
        // Create - reset form after success
        result = await proveedoresAPI.create(currentFormData);
        // Reset form for next entry
        setFormData(emptyFormData);
        setOriginalFormData(emptyFormData);
      }

      setHasChanges(false);
      // Refresh suggestions and notify parent with the created proveedor codigo
      loadProveedores();
      dataRefresh.emit("proveedores");
      onProveedorSaved?.(result.codigo);
      return result;
    })();

    toast.promise(savePromise, {
      loading: currentFormData.codigo ? "Actualizando..." : "Creando proveedor...",
      success: (data) => `Proveedor ${data.codigo} guardado correctamente`,
      error: (err) => `Error: ${err.message}`
    });
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);
    const deletePromise = (async () => {
      await proveedoresAPI.delete(formData.codigo);

      setFormData({
        codigo: "",
        nombreRazonSocial: "",
        cuit: "",
        domicilio: "",
        localidad: "",
        provincia: "",
        personaContacto: "",
        telefono: "",
        celular: "",
        whatsapp: "",
        email: "",
        saldo: 0,
        activo: true
      });
      setOriginalFormData({
        codigo: "",
        nombreRazonSocial: "",
        cuit: "",
        domicilio: "",
        localidad: "",
        provincia: "",
        personaContacto: "",
        telefono: "",
        celular: "",
        whatsapp: "",
        email: "",
        saldo: 0,
        activo: true
      }); // Reset to emptyish
      setHasChanges(false);
      loadProveedores();
      dataRefresh.emit("proveedores");
    })();

    toast.promise(deletePromise, {
      loading: "Eliminando proveedor...",
      success: "Proveedor eliminado",
      error: (err: unknown) =>
        mensajeBajaBloqueada(err, "Error al eliminar proveedor"),
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código</label>
          <input
            type="text"
            value={formData.codigo}
            readOnly
            tabIndex={-1}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            placeholder="Auto-generado"
          />
        </div>
        <div className="relative">
          <label className="block text-sm font-medium mb-1">Nombre o Razón Social</label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              {createOnly ? (
                <input
                  type="text"
                  value={formData.nombreRazonSocial}
                  onChange={(e) => handleNombreChange(e.target.value)}
                  placeholder="Ingrese nombre o razón social..."
                  tabIndex={1}
                  className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                />
              ) : (
                <SearchInput
                  value={formData.nombreRazonSocial}
                  onChange={(e) => handleNombreChange(e.target.value)}
                  onKeyDown={handleNombreKeyDown}
                  onFocus={() => {
                    if (formData.nombreRazonSocial.trim() && filteredProveedores.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="Buscar proveedor existente..."
                  tabIndex={1}
                />
              )}
              {!createOnly && showSuggestions && filteredProveedores.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredProveedores.map((proveedor) => (
                    <button
                      key={proveedor.codigo}
                      type="button"
                      onClick={() => selectProveedor(proveedor)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-sm flex justify-between items-center"
                    >
                      <span>{proveedor.nombreRazonSocial}</span>
                      <span className="text-xs text-muted-foreground">{proveedor.codigo}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={handleNuevo}
              variant="outline"
              size="sm"
              className="h-10 self-end whitespace-nowrap"
              disabled={!!formData.codigo}
            >
              + Nuevo
            </Button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">CUIT</label>
          <input
            type="text"
            value={formData.cuit}
            onChange={(e) => handleInputChange("cuit", formatCUIT(e.target.value))}
            placeholder="XX-XXXXXXXX-X"
            maxLength={13}
            tabIndex={2}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Domicilio</label>
          <input
            type="text"
            value={formData.domicilio}
            onChange={(e) => handleInputChange("domicilio", e.target.value)}
            tabIndex={3}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Localidad</label>
          <input
            type="text"
            value={formData.localidad}
            onChange={(e) => handleInputChange("localidad", e.target.value)}
            tabIndex={4}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Provincia</label>
          <input
            type="text"
            value={formData.provincia}
            onChange={(e) => handleInputChange("provincia", e.target.value)}
            tabIndex={5}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Persona de Contacto</label>
          <input
            type="text"
            value={formData.personaContacto}
            onChange={(e) => handleInputChange("personaContacto", e.target.value)}
            tabIndex={6}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Teléfono</label>
          <input
            type="text"
            value={formData.telefono}
            onChange={(e) => handleInputChange("telefono", e.target.value)}
            tabIndex={7}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Celular</label>
          <input
            type="text"
            value={formData.celular}
            onChange={(e) => handleInputChange("celular", e.target.value)}
            tabIndex={8}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">WhatsApp</label>
          <input
            type="text"
            value={formData.whatsapp}
            onChange={(e) => handleInputChange("whatsapp", e.target.value)}
            tabIndex={9}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">E-mail</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            tabIndex={10}
            className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Saldo</label>
          <input
            type="text"
            value={`$${formData.saldo.toLocaleString()}`}
            readOnly
            tabIndex={-1}
            className={`w-full px-4 py-2 border border-input bg-input rounded-lg font-semibold ${formData.saldo > 0 ? 'text-green-600' : formData.saldo < 0 ? 'text-red-600' : 'text-green-600'
              }`}
          />
        </div>
      </div>
      <FormFooter
        onDelete={!createOnly ? handleDelete : undefined}
        deleteDisabled={!formData.codigo}
        deleteTabIndex={12}
        onSave={handleSave}
        saveDisabled={!hasChanges}
        saveTabIndex={11}
      />
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        if (!open) {
          handleCancelDelete();
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro que deseas eliminar este proveedor?
              No se puede eliminar si tiene pedidos, remitos, pagos o saldo.
              Si la baja procede, se conservan código e identificador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}